import { useEffect, useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { collection, getDocs, query, where, orderBy, limit, doc, updateDoc, increment } from "firebase/firestore";
import { db } from "../firebase";

export default function BannerCarousel({ isPremium = false }) {
  const [banners, setBanners] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const loadBanners = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "banners"), where("active", "==", true), orderBy("order", "asc"), limit(10))
        );
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const userType = isPremium ? "premium" : "free";
        setBanners(all.filter((b) => !b.audience || b.audience === "all" || b.audience === userType));
      } catch {
        try {
          const snap = await getDocs(query(collection(db, "banners"), where("active", "==", true), limit(10)));
          const userType = isPremium ? "premium" : "free";
          setBanners(
            snap.docs.map((d) => ({ id: d.id, ...d.data() }))
              .filter((b) => !b.audience || b.audience === "all" || b.audience === userType)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
          );
        } catch { setBanners([]); }
      }
    };
    loadBanners();
  }, [isPremium]);

  // Auto-rotate every 4s
  useEffect(() => {
    if (banners.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setActiveIndex((i) => (i + 1) % banners.length);
    }, 4000);
    return () => clearInterval(intervalRef.current);
  }, [banners.length]);

  const handleClick = async (banner) => {
    try { await updateDoc(doc(db, "banners", banner.id), { clicks: increment(1) }); } catch {}
    if (banner.redirectUrl) window.open(banner.redirectUrl, "_blank", "noopener,noreferrer");
  };

  const goTo = (idx) => {
    if (idx === activeIndex) return;
    setActiveIndex(idx);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveIndex((i) => (i + 1) % banners.length);
    }, 4000);
  };

  if (banners.length === 0) return null;

  return (
    <section className="mt-5">
      <div className="relative overflow-hidden rounded-2xl">
        {/* First image sets the natural height, rest are absolute on top */}
        {banners.map((banner, idx) => (
          <img
            key={banner.id}
            src={banner.imageUrl}
            alt={banner.title || "Ad"}
            onClick={() => handleClick(banner)}
            onLoad={() => { if (idx === 0) setLoaded(true); }}
            className={`w-full h-auto rounded-2xl cursor-pointer select-none transition-opacity duration-1000 ease-in-out ${
              idx === 0 ? "relative" : "absolute inset-0"
            } ${idx === activeIndex ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            draggable={false}
          />
        ))}

        {/* Swipe area for mobile */}
        {loaded && banners.length > 1 && (
          <div
            className="absolute inset-0 z-10 sm:hidden"
            onTouchStart={(e) => { e.currentTarget._startX = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              const diff = e.changedTouches[0].clientX - (e.currentTarget._startX || 0);
              if (diff < -40) goTo((activeIndex + 1) % banners.length);
              else if (diff > 40) goTo((activeIndex - 1 + banners.length) % banners.length);
              else handleClick(banners[activeIndex]);
            }}
          />
        )}

        {/* Arrows — desktop only */}
        {loaded && banners.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); goTo((activeIndex - 1 + banners.length) % banners.length); }}
              className="absolute left-3 top-1/2 z-20 -translate-y-1/2 hidden sm:flex h-9 w-9 items-center justify-center rounded-full bg-white/70 text-ink-800 shadow backdrop-blur-sm transition hover:bg-white"
              aria-label="Previous"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); goTo((activeIndex + 1) % banners.length); }}
              className="absolute right-3 top-1/2 z-20 -translate-y-1/2 hidden sm:flex h-9 w-9 items-center justify-center rounded-full bg-white/70 text-ink-800 shadow backdrop-blur-sm transition hover:bg-white"
              aria-label="Next"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}

        {/* Dots */}
        {loaded && banners.length > 1 && (
          <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-1.5">
            {banners.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => goTo(idx)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === activeIndex ? "w-6 bg-white shadow-md" : "w-2 bg-white/50"
                }`}
                aria-label={`Banner ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
